import type { RetornoLinha, Turno } from '@pcp/contracts';
import { useTurnos, useUpdateTurnos } from '../../api/cadastros';
import { type Column, EditableTable } from '../../components/EditableTable';
import { ErrorState, LoadingBlock } from '../../components/states';
import { t } from '../../i18n/pt-BR';
import { num } from './shared';

const c = t.cadastros.shifts;

/** Turnos A, B e C: cadastro fixo. As horas entram no cálculo; o horário é só referência. */
export function TurnosTab({ canEdit }: { canEdit: boolean }) {
  const query = useTurnos();
  const update = useUpdateTurnos();

  const columns: Column<Turno>[] = [
    { key: 'turno', header: c.code, read: (r) => <strong>{r.turno}</strong> },
    {
      key: 'descricao',
      header: c.description,
      read: (r) => r.descricao,
      edit: { kind: 'text', field: 'descricao', width: 150 },
    },
    {
      key: 'hora-ini',
      header: c.start,
      read: (r) => r['hora-ini'],
      edit: { kind: 'text', field: 'hora-ini', width: 80 },
    },
    {
      key: 'hora-fim',
      header: c.end,
      read: (r) => r['hora-fim'],
      edit: { kind: 'text', field: 'hora-fim', width: 80 },
    },
    {
      key: 'horas',
      header: c.hours,
      numeric: true,
      read: (r) => num(r.horas, 2),
      edit: { kind: 'number', field: 'horas', step: 0.01, min: 0.01, max: 24 },
    },
    {
      key: 'ativo',
      header: c.active,
      read: (r) => (r.ativo ? t.cadastros.yes : t.cadastros.no),
      edit: { kind: 'check', field: 'ativo' },
    },
  ];

  const save = async (row: Turno, changes: Partial<Turno>): Promise<RetornoLinha> => {
    const [r] = await update.mutateAsync([{ ...changes, turno: row.turno, version: row.version }]);
    return r!;
  };

  return (
    <section className="panel" aria-labelledby="tab-shifts">
      <h2 id="tab-shifts">{t.cadastros.tabs.shifts}</h2>
      <p className="hint">{c.hint}</p>
      {query.isLoading ? <LoadingBlock rows={2} /> : null}
      {query.error ? <ErrorState error={query.error} onRetry={() => void query.refetch()} /> : null}
      {query.data ? (
        <EditableTable
          rows={query.data}
          rowKey={(r) => r.turno}
          rowLabel={(r) => `${t.cadastros.shifts.code} ${r.turno}`}
          columns={columns}
          canEdit={canEdit}
          onSave={save}
          onReload={() => void query.refetch()}
        />
      ) : null}
    </section>
  );
}
